import { useEffect, useRef } from 'react';

const TITLE_PLACEHOLDER = '{title}';

/**
 * Hook to update document.title based on a configured pattern.
 *
 * When `pattern` is null, the hook is a no-op — the host page's title is preserved.
 * When `pattern` is set, the hook replaces `{title}` with `currentTitle` and writes
 * the result to `document.title`. If `currentTitle` is null (e.g. bare help index
 * with no question or category), the original host title is restored.
 *
 * On mount the original `document.title` is captured. On unmount it is restored.
 *
 * @param {string|null} currentTitle - resolved title source for this view
 *   (question body, category name, or null on bare index).
 * @param {string|null} pattern - admin-configured template string with a `{title}`
 *   placeholder, or null to disable.
 */
export const useDocumentTitle = (
  currentTitle: string | null,
  pattern: string | null,
): void => {
  // capture the original title once, on first render
  const originalTitleRef = useRef<string | null>(null);
  if (originalTitleRef.current === null) {
    originalTitleRef.current = document.title;
  }

  // restore the original title on unmount
  useEffect(() => {
    return () => {
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
      }
    };
  }, []);

  // write document.title whenever the resolved title or pattern changes
  useEffect(() => {
    if (pattern == null) {
      // hook disabled — leave document.title alone
      return;
    }
    if (currentTitle == null) {
      // no value to inject — restore the original host title
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
      }
      return;
    }
    document.title = pattern.replace(TITLE_PLACEHOLDER, currentTitle);
  }, [pattern, currentTitle]);
};
